import type { CartItemOutputDto, ItemPriceResultDto, ItemQuantityPriceDto } from "@sticker-giant/sg-frontend-common";
import { PackagingMode, resolvePackagingMode } from "@/utils/resolvePackagingMode";
import {
  buildRollOptions,
  carryOverRollSelection,
  getRollDriver,
  needsRollSelection,
  type RollDriver,
  type RollSelection,
} from "@/utils/rollQuantityUtils";
import { buildStackOptions, toValidStackOption } from "@/utils/stackQuantityUtils";

export interface CartPackagingOption {
  rollCount: number;
  rollQty: number;
  diameterInches?: number;
}

/** Rolls ignore backend default — `selectedOption` is `null` until the item has a matching roll choice */
export type CartPackagingSelection =
  | { mode: PackagingMode.Roll; options: CartPackagingOption[]; selectedOption: CartPackagingOption | null }
  | {
      mode: PackagingMode.Bundle;
      options: CartPackagingOption[];
      selectedOption: CartPackagingOption;
      defaultOption: CartPackagingOption;
    };

function getRollOptions(quantityPrice: ItemQuantityPriceDto | undefined, driver: RollDriver): CartPackagingOption[] {
  return buildRollOptions(quantityPrice?.rollLayout, driver).map(option => ({
    rollCount: option.numRolls,
    rollQty: option.itemsPerRoll,
    diameterInches: option.outsideDiameter ? Number(option.outsideDiameter) : undefined,
  }));
}

function getStackOptions(quantity: number, quantityPrice: ItemQuantityPriceDto | undefined) {
  const stackLayout = quantityPrice?.stackLayout;
  const defaultStack = toValidStackOption(stackLayout?.defaultStackOption);
  const built = buildStackOptions({
    selectedQuantity: quantity,
    minStacks: stackLayout?.minNumberOfStacks,
    maxStacks: stackLayout?.maxNumberOfStacks,
    defaultStackOption: defaultStack,
  });

  const options = built.map(option => ({ rollCount: option.numStacks, rollQty: option.itemsPerStack }));

  if (!defaultStack) return options;

  const defaultOption = {
    rollCount: defaultStack.numStacks,
    rollQty: defaultStack.itemsPerStack,
  };
  const hasDefaultOption = options.some(
    option => option.rollCount === defaultOption.rollCount && option.rollQty === defaultOption.rollQty,
  );

  if (hasDefaultOption) return options;

  return [defaultOption, ...options];
}

function getDefaultBundleOption(quantityPrice: ItemQuantityPriceDto | undefined) {
  const defaultStack = quantityPrice?.stackLayout?.defaultStackOption;
  if (!defaultStack || defaultStack.numStacks === undefined || defaultStack.itemsPerStack === undefined) {
    return undefined;
  }

  return {
    rollCount: defaultStack.numStacks,
    rollQty: defaultStack.itemsPerStack,
  };
}

function findSelectedOption(item: CartItemOutputDto, options: CartPackagingOption[]) {
  if (item.rollCount === null || item.rollQty === null) return undefined;

  return options.find(option => option.rollCount === item.rollCount && option.rollQty === item.rollQty);
}

export function resolveCartPackagingSelection({
  item,
  quantity,
  pricingData,
}: {
  item: CartItemOutputDto;
  quantity: number;
  pricingData?: ItemPriceResultDto;
}): CartPackagingSelection | undefined {
  const mode = resolvePackagingMode(item.sku.finish);
  if (!mode) return undefined;

  const quantityPrice = pricingData?.quantityPrices.find(price => price.quantity === quantity);
  if (mode === PackagingMode.Roll) {
    // Rolls stay selectable without options (only the min option, which Fewest rolls replaces), but not before pricing
    if (!quantityPrice) return undefined;
    const options = getRollOptions(quantityPrice, getRollDriver(item));
    return { mode, options, selectedOption: findSelectedOption(item, options) ?? null };
  }

  const options = getStackOptions(quantity, quantityPrice);
  if (options.length === 0) return undefined;

  const matchedSelection = findSelectedOption(item, options);
  const defaultOption = getDefaultBundleOption(quantityPrice) ?? options[0];
  return { mode, options, selectedOption: matchedSelection ?? defaultOption, defaultOption };
}

/**
 * Given a quantity, it returns which set of pricing data (either preset or custom) contains the information related to
 * that quantity. Returns `undefined` if no match is found.
 */
export function getPricingDataForQuantity(
  quantity: number,
  presetData?: ItemPriceResultDto,
  customData?: ItemPriceResultDto,
) {
  const presetMatch = presetData?.quantityPrices.some(quantityPrice => quantityPrice.quantity === quantity);
  if (presetMatch) return presetData;

  const customMatch = customData?.quantityPrices.some(quantityPrice => quantityPrice.quantity === quantity);
  if (customMatch) return customData;

  return undefined;
}

/** Rolls carry over on the item's driver; bundles always return null selections */
export function resetPackagingForQuantity({
  item,
  quantity,
  pricingData,
}: {
  item: CartItemOutputDto;
  quantity: number;
  pricingData?: ItemPriceResultDto;
}): RollSelection {
  if (resolvePackagingMode(item.sku.finish) === PackagingMode.Roll) {
    const rollLayout = pricingData?.quantityPrices.find(price => price.quantity === quantity)?.rollLayout;
    return carryOverRollSelection(buildRollOptions(rollLayout, getRollDriver(item)), getRollDriver(item), item);
  }
  return { rollCount: null, rollQty: null };
}

/** Whether an explicit packaging choice could not be carried over to `packaging`. */
export function lostPackagingSelection(item: CartItemOutputDto, packaging: RollSelection) {
  // Only a previous roll quantity can be lost; null values were already No Preference
  if (resolvePackagingMode(item.sku.finish) !== PackagingMode.Roll) return false;
  return needsRollSelection(
    { rollDriver: getRollDriver(item), rollCount: packaging.rollCount },
    item.rollCount !== null,
  );
}

/** The write needed to bring a roll item's stored values in line with its current options; `undefined` when none is. */
export function reconcileRollSelection(
  item: CartItemOutputDto,
  pricingData?: ItemPriceResultDto,
): { packaging: RollSelection; lost: boolean } | undefined {
  const selection = resolveCartPackagingSelection({ item, quantity: item.quantity, pricingData });
  if (selection?.mode !== PackagingMode.Roll || selection.selectedOption) return undefined;

  const driver = getRollDriver(item);
  if (driver === null || driver === "FEWEST_ROLLS") return undefined;
  if (item.rollCount === null || item.rollQty === null) return undefined;

  const packaging = resetPackagingForQuantity({ item, quantity: item.quantity, pricingData });
  return { packaging, lost: lostPackagingSelection(item, packaging) };
}
